import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./hooks/redux-hooks";
import { getUser } from "./slices/authSlice";
import { useEffect } from "react";
import { SnackbarProvider } from "notistack";
import { SnackbarUtilsConfigurator } from "./components/common/notificationHelper";
import './App.css';

// Layouts & Pages
import Login from "./pages/Login";
import DefaultLayout from "./layouts/DefaultLayout";
import ProtectedLayout from "./layouts/ProtectedLayout";
import UsersPage from "./pages/management/users/UsersPage";
import NotificationsTemplatePage from "./pages/management/notifications/NotificationsTemplatePage";
import ChangelogsManagementPage from "./pages/management/changelogs/ChangelogsManagementPage";
import PatientAppointmentsPage from "./pages/appointment/PatientAppointmentsPage";
import PatientsPage from "./pages/patients/PatientsPage";
import ChangelogPage from "./pages/changelog/ChangelogPage";
import ProfilePage from "./pages/profile/ProfilePage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import HomePage from "./pages/home/HomePage";
import ExaminationPage from "./pages/examination/ExaminationsPage";

// Spinner
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

function App() {
  const dispatch = useAppDispatch();
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const userProfileInfo = useAppSelector((state) => state.auth.userProfileData);
  const status = useAppSelector((state) => state.auth.status);
  const location = useLocation();

  useEffect(() => {
    if (basicUserInfo && !userProfileInfo && status === "idle") {
      dispatch(getUser(basicUserInfo.id));
    }
  }, [basicUserInfo, userProfileInfo, status, dispatch]);

  // 🛑 1. If no user is logged in → redirect to login
  if (!basicUserInfo && location.pathname !== "/login") {
    return <Navigate to="/login" replace />;
  }

  // ⏳ 2. While waiting for user profile, show spinner
  if (basicUserInfo && !userProfileInfo && status === "loading") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress size={48} color="primary" />
      </Box>
    );
  }

  return (
    <SnackbarProvider maxSnack={3}>
      <SnackbarUtilsConfigurator />

      <Routes>
        {/* Public routes */}
        <Route element={<DefaultLayout />}>
          <Route path="/login" element={<Login />} />
        </Route>

        {/* Protected routes */}
        {basicUserInfo && userProfileInfo && (
          <Route element={<ProtectedLayout />}>
            {userProfileInfo.roles.includes("ADMIN") ? (
              <>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/management/users" element={<UsersPage />} />
                <Route path="/management/notifications" element={<NotificationsTemplatePage />} />
                <Route path="/management/changelogs" element={<ChangelogsManagementPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/patients" element={<PatientsPage />} />
                <Route path="/appointments" element={<PatientAppointmentsPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/examination/:patientId" element={<ExaminationPage />} />
              </>
            ) : (
              <>
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/patients" element={<PatientsPage />} />
                <Route path="/appointments" element={<PatientAppointmentsPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/examination/:patientId" element={<ExaminationPage />} />
              </>
            )}

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        )}
      </Routes>
    </SnackbarProvider>
  );
}

export default App;
